import * as React from "react";
import { cn } from "@/lib/utils";
import { Pencil, Check, X } from "lucide-react";

export interface DataFieldProps {
  label: string;
  value: string;
  className?: string;
  editable?: boolean;
  onSave?: (value: string) => void;
}

function DataField({
  label,
  value,
  className,
  editable = false,
  onSave,
}: DataFieldProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const cancelEdit = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const saveEdit = () => {
    if (draft !== value) {
      onSave?.(draft);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  return (
    <div
      className={cn(
        "inline-flex flex-col gap-1 text-zinc-900 tracking-tight",
        className,
      )}
    >
      <span className="uppercase text-xs">{label}</span>

      <div className="relative w-full">
        {isEditing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full pr-20 py-3 bg-white border-b-2 border-zinc-900 outline-none"
          />
        ) : (
          <span className="block w-full pr-20 py-3 bg-white border-b-2 border-zinc-900 min-h-6">
            {value}
          </span>
        )}

        {editable && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={saveEdit}
                  className="text-green-600 hover:text-green-800 cursor-pointer"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={cancelEdit}
                  className="text-red-600 hover:text-red-800 cursor-pointer"
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="text-zinc-500 hover:text-zinc-900 cursor-pointer"
              >
                <Pencil size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { DataField };
